import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Hairline,
  Input,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import {
  CompanyLocationPaymentMethodValues,
  type CompanyDetailDTO,
  type CompanyLocationDTO,
  type CompanyLocationPaymentMethod,
} from "@wmart/shared-types";

interface LocationFormValues {
  name: string;
  code: string;
  // Form input is a string (HTML number input) — coerced to int on submit.
  discount_percent: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    postcode: string;
    country: string;
  };
  allowed_payment_methods: CompanyLocationPaymentMethod[];
  buyer_can_edit_address: boolean;
}

const blankDefaults: LocationFormValues = {
  name: "",
  code: "",
  discount_percent: "0",
  address: {
    line1: "",
    line2: "",
    city: "",
    postcode: "",
    country: "GB",
  },
  allowed_payment_methods: [],
  buyer_can_edit_address: false,
};

/**
 * Single page that handles both `/companies/:id/locations/new` and
 * `/companies/:id/locations/:locId`, discriminated by `locId` presence.
 *
 * In edit mode we GET the parent CompanyDetailDTO and find the location
 * inside its `locations` array — no separate `GET /:locId` endpoint exists
 * (the admin company-locations controller exposes only POST/PATCH/DELETE).
 *
 * Address fields are nested under `address.*` matching the JSONB shape on
 * `company_locations.address_json` (verified against
 * `packages/shared-types/src/company.ts CompanyLocationAddress`) — Plan 08
 * L24 + T12 amendment carry-forward.
 */
export function CompanyLocationEdit() {
  const { id, locId } = useParams<{ id: string; locId?: string }>();
  const isNew = locId === undefined;
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<LocationFormValues>({
    defaultValues: blankDefaults,
  });

  useEffect(() => {
    if (isNew || !id || !locId) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await api.get<CompanyDetailDTO>(
          `/v1/admin/companies/${id}`,
        );
        if (cancelled) return;
        const found = detail.locations.find((l) => l.id === locId);
        if (!found) {
          setLoadError(true);
          notify("error", "Location not found");
          return;
        }
        reset({
          name: found.name,
          code: found.code,
          discount_percent: String(found.discount_percent),
          address: {
            line1: found.address.line1,
            line2: found.address.line2 ?? "",
            city: found.address.city,
            postcode: found.address.postcode,
            country: found.address.country,
          },
          allowed_payment_methods: found.allowed_payment_methods,
          buyer_can_edit_address: found.buyer_can_edit_address,
        });
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setLoadError(true);
        if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
        else notify("error", "Failed to load location");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, locId, isNew, reset, notify, notifyProblem]);

  async function onSubmit(values: LocationFormValues) {
    if (!id) return;
    const discount = Number(values.discount_percent);
    if (!Number.isInteger(discount) || discount < 0 || discount > 100) {
      notify("error", "Discount must be an integer between 0 and 100");
      return;
    }

    const trimmedLine2 = values.address.line2.trim();
    const body = {
      name: values.name,
      code: values.code,
      discount_percent: discount,
      address: {
        line1: values.address.line1,
        line2: trimmedLine2 === "" ? null : trimmedLine2,
        city: values.address.city,
        postcode: values.address.postcode,
        country: values.address.country.toUpperCase(),
      },
      allowed_payment_methods: values.allowed_payment_methods,
      buyer_can_edit_address: values.buyer_can_edit_address,
    };

    try {
      if (isNew) {
        await api.post<CompanyLocationDTO>(
          `/v1/admin/companies/${id}/locations`,
          body,
        );
        notify("success", "Location created");
        navigate(`/companies/${id}`);
      } else if (locId) {
        await api.patch<CompanyLocationDTO>(
          `/v1/admin/companies/${id}/locations/${locId}`,
          body,
        );
        notify("success", "Location saved");
        navigate(`/companies/${id}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    }
  }

  if (!id) {
    return <p className="text-body-sm text-danger">Missing company id.</p>;
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to={`/companies/${id}`}
          className="text-body-sm text-accent hover:underline"
        >
          ← Back to company
        </Link>
        <p className="text-body-sm text-danger">
          Location could not be loaded.
        </p>
      </div>
    );
  }

  if (!loaded) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <Link
        to={`/companies/${id}`}
        className="text-body-sm text-accent hover:underline"
      >
        ← Back to company
      </Link>

      <h1 className="font-serif text-display-sm text-ink-900">
        {isNew ? "New location" : "Edit location"}
      </h1>

      <Card className="p-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Name"
                error={errors.name?.message}
                {...register("name", {
                  required: "Name is required",
                  maxLength: { value: 200, message: "Max 200 characters" },
                })}
              />
            </div>
            <div className="w-48">
              <Input
                label="Code"
                placeholder="HQ-LDN"
                className="font-mono"
                error={errors.code?.message}
                {...register("code", {
                  required: "Code is required",
                  maxLength: { value: 64, message: "Max 64 characters" },
                })}
              />
            </div>
          </div>

          <div className="w-48">
            <Input
              type="number"
              min={0}
              max={100}
              step={1}
              label="Discount %"
              helper="Integer 0–100."
              {...register("discount_percent", { required: true })}
            />
          </div>

          <fieldset className="rounded-sm border border-ink-200 p-4">
            <legend className="px-1 text-body-sm font-medium text-ink-700">
              Address
            </legend>
            <div className="flex flex-col gap-4 pt-2">
              <Input
                label="Line 1"
                error={errors.address?.line1?.message}
                {...register("address.line1", {
                  required: "Line 1 is required",
                  maxLength: { value: 200, message: "Max 200 characters" },
                })}
              />

              <Input
                label="Line 2"
                placeholder="(optional)"
                {...register("address.line2", {
                  maxLength: { value: 200, message: "Max 200 characters" },
                })}
              />

              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    label="City"
                    error={errors.address?.city?.message}
                    {...register("address.city", {
                      required: "City is required",
                      maxLength: { value: 100, message: "Max 100 characters" },
                    })}
                  />
                </div>
                <div className="w-48">
                  <Input
                    label="Postcode"
                    error={errors.address?.postcode?.message}
                    {...register("address.postcode", {
                      required: "Postcode is required",
                      maxLength: { value: 20, message: "Max 20 characters" },
                    })}
                  />
                </div>
                <div className="w-32">
                  <Input
                    label="Country"
                    placeholder="GB"
                    maxLength={2}
                    className="uppercase"
                    error={errors.address?.country?.message}
                    {...register("address.country", {
                      required: "Country is required",
                      pattern: {
                        value: /^[A-Za-z]{2}$/,
                        message: "ISO 2-letter country code",
                      },
                    })}
                  />
                </div>
              </div>
            </div>
          </fieldset>

          <fieldset className="rounded-sm border border-ink-200 p-4">
            <legend className="px-1 text-body-sm font-medium text-ink-700">
              Allowed payment methods
            </legend>
            <div className="flex flex-col gap-2 pt-2">
              {CompanyLocationPaymentMethodValues.map((m) => (
                <Checkbox
                  key={m}
                  value={m}
                  label={m}
                  {...register("allowed_payment_methods")}
                />
              ))}
            </div>
          </fieldset>

          <Checkbox
            label="Buyer can edit address at checkout"
            {...register("buyer_can_edit_address")}
          />

          <Hairline />

          <div>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isNew ? "Create" : "Save"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
